import * as React from 'react';
import * as ReactDOM from 'react-dom';
import ComposeEmailFunction from '../ComposeEmailFunction';
import fetchBrandList from '../services/fetchBrandList';
import initializeLivePersonaCard from '../actions/initializeLivePersonaCard';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import PersonaCard from './PersonaCard';
import personaControlStore from '../store/Store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isEmailAddressBrand, isEmailAddressDecoratedBrand } from '../utils/isEmailAddressBrand';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { logUsage } from 'owa-analytics';
import { lokiClientCorrelationId } from 'owa-people-loki';
import { observer } from 'mobx-react';

export interface PersonaCardBehaviorInputProps {
    emailAddress: string;
    name: string;
    olsPersonaId?: string;
    personaType?: string;
    composeEmailOverride?: ComposeEmailFunction;
    disableHover?: boolean;
    ariaLabel?: string;
    className?: string;
    mailBoxType?: string;
    isUnauthenticatedSender?: boolean;
    locationToOpen?: string;
    clientScenario?: string;
    focusReturnElement?: HTMLElement;
    onCardClose?: () => void;
}

export interface PersonaCardBehaviorProps extends PersonaCardBehaviorInputProps {
    onPersonaCardBehaviorClick?: (ev: React.MouseEvent<EventTarget>) => void;
    onPersonaCardBehaviorMouseEnter?: (ev: React.MouseEvent<EventTarget>) => void;
    onPersonaCardBehaviorMouseLeave?: (ev: React.MouseEvent<EventTarget>) => void;
    tabIndex?: number;
}

interface PersonaCardBehaviorInternalProps extends PersonaCardBehaviorProps {
    InputTemplate: React.ComponentClass<any>;
}

const HOVER_DELAY_MS = 500;

const LivePersonaCardPersonaTypes = {
    NotResolved: 'NotResolved',
    User: 'User',
    Group: 'Group',
    External: 'External',
    DistributionList: 'DistributionList',
    PrivateDistributionList: 'PrivateDistributionList',
    Location: 'Location',
    Room: 'Room',
    Meeting: 'Meeting',
    Brand: 'Brand',
};

interface PersonaInfo {
    personaType: string;
    isBrand: boolean;
    isTier3Brand: boolean;
}

let personaCardDiv: HTMLElement;

class LegacyPersonaCardBehavior extends React.Component<PersonaCardBehaviorInternalProps, {}> {
    private wrappedElement: HTMLElement;
    private hoverTimerId: any;

    constructor(props: PersonaCardBehaviorInternalProps) {
        super(props);
    }

    private onElementClicked = (ev: React.MouseEvent<EventTarget>) => {
        ev.stopPropagation();
        this.openPersonaCard();
    };

    private openPersonaCard = () => {
        if (!personaCardDiv) {
            personaCardDiv = document.createElement('div');
            personaCardDiv.id = 'personaCard';
            document.body.appendChild(personaCardDiv);
        }

        let { name, emailAddress } = this.props;
        if (!name) {
            name = emailAddress;
        }
        if (name && emailAddress) {
            ReactDOM.render(
                <PersonaCard
                    emailAddress={emailAddress}
                    name={name}
                    targetElement={this.wrappedElement}
                    onDismiss={this.closePersonaCard}
                />,
                personaCardDiv
            );
        }
    };

    private closePersonaCard = (ev?: any) => {
        if (this.props.focusReturnElement) {
            this.props.focusReturnElement.focus();
        }

        ReactDOM.unmountComponentAtNode(personaCardDiv);
    };

    private onElementHover = () => {
        this.openPersonaCard();
    };

    private onElementMouseEnter = (ev: React.MouseEvent<EventTarget>) => {
        this.hoverTimerId = setTimeout(this.onElementHover, HOVER_DELAY_MS);
    };

    private onElementMouseLeave = (ev: React.MouseEvent<EventTarget>) => {
        if (this.hoverTimerId) {
            clearTimeout(this.hoverTimerId);
            this.hoverTimerId = undefined;
        }
    };

    private getElement = wrappedElement => {
        this.wrappedElement = ReactDOM.findDOMNode(wrappedElement) as HTMLElement;
    };

    render() {
        return (
            <this.props.InputTemplate
                {...this.props}
                onPersonaCardBehaviorClick={this.onElementClicked}
                onPersonaCardBehaviorMouseEnter={this.onElementMouseEnter}
                onPersonaCardBehaviorMouseLeave={this.onElementMouseLeave}
                ref={this.getElement}
            />
        );
    }
}

@observer
class LivePersonaCardBehavior extends React.Component<PersonaCardBehaviorInternalProps, {}> {
    componentDidMount() {
        initializeLivePersonaCard(this.props.composeEmailOverride);
        fetchBrandList();
    }

    render() {
        if (personaControlStore.isLivePersonaCardInitialized) {
            let personaInfo = calculatePersonaType(
                this.props.isUnauthenticatedSender,
                this.props.mailBoxType,
                this.props.personaType,
                this.props.emailAddress
            );
            if (isFeatureEnabled('peo-lpcTargetV2OnPersonaCardBehavior')) {
                let LivePersonaCardHoverTargetV2 = (window as any).LivePersonaCardHoverTargetV2;
                return (
                    <LivePersonaCardHoverTargetV2
                        tabIndex={this.props.tabIndex}
                        cardParameters={{
                            externalAppSessionCorrelationId: lokiClientCorrelationId,
                            personaInfo: {
                                identifiers: {
                                    Smtp: this.props.emailAddress,
                                    PersonaType: personaInfo.personaType,
                                    OlsPersonaId: this.props.olsPersonaId,
                                },
                                isUnauthenticatedSender: this.props.isUnauthenticatedSender,
                                displayName: this.props.name,
                                isTier3Brand: personaInfo.isTier3Brand,
                            },
                            behavior: {
                                disableHover: this.props.disableHover,
                                onCardOpen: () => {
                                    logInfoOnCardOpen(
                                        personaInfo,
                                        this.props.clientScenario,
                                        this.props.locationToOpen
                                    );
                                },
                                onCardClose: () => {
                                    if (this.props.focusReturnElement) {
                                        this.props.focusReturnElement.focus();
                                    }

                                    if (this.props.onCardClose) {
                                        this.props.onCardClose();
                                    }
                                },
                                locationToOpen: this.props.locationToOpen,
                                clientScenario: this.props.clientScenario,
                            },
                            ariaLabel: this.props.ariaLabel,
                        }}>
                        <span className={this.props.className} aria-label={this.props.ariaLabel}>
                            <this.props.InputTemplate {...this.props} />
                        </span>
                    </LivePersonaCardHoverTargetV2>
                );
            } else {
                let LivePersonaCardHoverTarget = (window as any).LivePersonaCardHoverTarget;
                return (
                    <LivePersonaCardHoverTarget
                        tabIndex={this.props.tabIndex}
                        upn={this.props.emailAddress}
                        externalAppSessionCorrelationId={lokiClientCorrelationId}
                        disableHover={this.props.disableHover}
                        onCardOpen={() => {
                            logInfoOnCardOpen(
                                personaInfo,
                                this.props.clientScenario,
                                this.props.locationToOpen
                            );
                        }}
                        onCardClose={() => {
                            if (this.props.focusReturnElement) {
                                this.props.focusReturnElement.focus();
                            }
                        }}
                        hostAppPersonaInfo={{
                            PersonaType: personaInfo.personaType,
                            PersonaDisplayName: this.props.name,
                            OlsPersonaId: this.props.olsPersonaId,
                            isUnauthenticatedSender: this.props.isUnauthenticatedSender,
                        }}
                        clientScenario={this.props.clientScenario}
                        isTier3Brand={personaInfo.isTier3Brand}>
                        <span className={this.props.className} aria-label={this.props.ariaLabel}>
                            <this.props.InputTemplate {...this.props} />
                        </span>
                    </LivePersonaCardHoverTarget>
                );
            }
        } else {
            return <this.props.InputTemplate {...this.props} />;
        }
    }
}

function logInfoOnCardOpen(
    personaInfo: PersonaInfo,
    clientScenario?: string,
    locationToOpen?: string
) {
    if (personaInfo.isBrand) {
        logUsage(
            personaInfo.personaType === LivePersonaCardPersonaTypes.Brand
                ? 'BrandCardDisplayed'
                : 'BrandDetectedButNotDisplayed'
        );
    }

    const scenarioToPrint = clientScenario || 'No Client Scenario';
    const locationToPrint = locationToOpen || 'LPC';
    logUsage('LPCRenderedFromHoverTarget', {
        clientScenario: scenarioToPrint,
        locationToOpen: locationToPrint,
    });
}

function calculatePersonaType(
    isUnauthenticatedSender: boolean,
    mailBoxType: string,
    personaType: string,
    emailAddress: string
): PersonaInfo {
    if (isUnauthenticatedSender) {
        return {
            personaType: LivePersonaCardPersonaTypes.User,
            isBrand: false,
            isTier3Brand: false,
        };
    }

    if (
        isFeatureEnabled('rp-brandCards') &&
        !isNullOrWhiteSpace(emailAddress) &&
        isEmailAddressBrand(emailAddress)
    ) {
        const isTier3Brand = isEmailAddressDecoratedBrand(emailAddress);
        return {
            personaType: LivePersonaCardPersonaTypes.Brand,
            isBrand: true,
            isTier3Brand: isTier3Brand,
        };
    }

    if (!isNullOrWhiteSpace(mailBoxType)) {
        return {
            personaType: getLivePersonaCardPersonaTypeFromMailBoxType(mailBoxType),
            isBrand: false,
            isTier3Brand: false,
        };
    }

    if (
        !isNullOrWhiteSpace(personaType) &&
        Object.keys(LivePersonaCardPersonaTypes).indexOf(personaType) > -1
    ) {
        return { personaType: personaType, isBrand: false, isTier3Brand: false };
    }

    if (isConsumer()) {
        return { personaType: 'User', isBrand: false, isTier3Brand: false };
    }

    return { personaType: 'NotResolved', isBrand: false, isTier3Brand: false };
}

function getLivePersonaCardPersonaTypeFromMailBoxType(mailBoxType: string): string {
    switch (mailBoxType) {
        case 'PublicDL':
            return LivePersonaCardPersonaTypes.DistributionList;
        case 'PrivateDL':
            return LivePersonaCardPersonaTypes.PrivateDistributionList;
        case 'GroupMailbox':
            return LivePersonaCardPersonaTypes.Group;
        case 'Mailbox':
        case 'Contact':
            return LivePersonaCardPersonaTypes.User;
        case 'OneOff':
        case 'Unknown':
            return LivePersonaCardPersonaTypes.NotResolved;
        default:
            return LivePersonaCardPersonaTypes.User;
    }
}

export function personaCardBehavior<T extends PersonaCardBehaviorProps>(
    InputTemplate: React.ComponentClass<T>
): any {
    return observer(
        class extends React.Component<T, {}> {
            render() {
                if (personaControlStore.isPersonaCardDisabled) {
                    return <InputTemplate {...this.props} />;
                } else {
                    let ComponentClass =
                        !isFeatureEnabled('fwk-partner-code-off') &&
                        isFeatureEnabled('rp-livePersonaCard')
                            ? LivePersonaCardBehavior
                            : LegacyPersonaCardBehavior;

                    return <ComponentClass InputTemplate={InputTemplate} {...this.props} />;
                }
            }
        }
    );
}
